/**
 * Error logging utility with automatic sensitive data sanitization
 * @module utils/error-logger
 *
 * Provides structured error logging that prevents exposure of:
 * - Budget amounts
 * - Client names
 * - User emails
 * - Authentication tokens
 * - Stack traces in production
 */

/**
 * Structured log entry format
 */
export interface ErrorLogEntry {
  timestamp: string
  page: string
  action: string
  errorType: string
  message: string
  statusCode?: number
  errorId?: string
}

/**
 * Patterns for detecting sensitive data in strings
 */
const SENSITIVE_PATTERNS = {
  // Email addresses
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,

  // Currency amounts (BRL format with R$ or numbers with commas/periods)
  currency: /R\$\s*[\d.,]+|\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?/g,

  // Large numbers (likely budget amounts - 4+ digits)
  largeNumbers: /\b\d{4,}\b/g,

  // JWT tokens
  jwtToken: /eyJ[A-Za-z0-9-_]+\.eyJ[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+/g,

  // Bearer tokens
  bearerToken: /Bearer\s+[A-Za-z0-9-_.]+/gi,

  // AWS access keys
  awsAccessKey: /AKIA[0-9A-Z]{16}/g,

  // Client codes (format: 000054, 000025, etc.)
  clientCode: /\b\d{6}\b/g,

  // Stack traces
  stackTrace: /\s+at\s+.*/g,

  // File paths
  filePath: /\/[\w/.-]+|[A-Z]:\\[\w\\.-]+/g,

  // URLs with sensitive query params
  urlWithParams: /https?:\/\/[^\s]+\?[^\s]+/g
} as const

/**
 * Fields that should be removed from error objects
 */
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'secret',
  'authorization',
  'cookie',
  'session',
  'creditCard',
  'ssn',
  'taxId',
  'budget',
  'revenue',
  'cost',
  'amount',
  'salary',
  'email',
  'phone'
] as const

/**
 * Sanitize a string by removing sensitive data
 */
function sanitizeString(value: string): string {
  let sanitized = value

  // Replace emails
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.email, '[EMAIL]')

  // Replace currency amounts
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.currency, '[AMOUNT]')

  // Replace large numbers (likely budget values)
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.largeNumbers, '[NUMBER]')

  // Replace client codes
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.clientCode, '[CLIENT_CODE]')

  // Replace JWT tokens
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.jwtToken, '[JWT_TOKEN]')

  // Replace Bearer tokens
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.bearerToken, '[AUTH_TOKEN]')

  // Replace AWS keys
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.awsAccessKey, '[AWS_KEY]')

  // Replace stack traces (only in production)
  if (import.meta.env.PROD) {
    sanitized = sanitized.replace(SENSITIVE_PATTERNS.stackTrace, '')
  }

  // Replace file paths
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.filePath, '[PATH]')

  // Replace URLs with query params
  sanitized = sanitized.replace(SENSITIVE_PATTERNS.urlWithParams, '[URL]')

  return sanitized.trim()
}

/**
 * Recursively sanitize an object by removing sensitive fields
 */
function sanitizeObject(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (typeof obj === 'string') {
    return sanitizeString(obj)
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item))
  }

  if (typeof obj === 'object') {
    const sanitized: Record<string, any> = {}

    for (const [key, value] of Object.entries(obj)) {
      // Skip sensitive fields entirely
      if (SENSITIVE_FIELDS.includes(key.toLowerCase() as any)) {
        sanitized[key] = '[REDACTED]'
        continue
      }

      // Recursively sanitize nested objects
      sanitized[key] = sanitizeObject(value)
    }

    return sanitized
  }

  return obj
}

/**
 * Extract error type from error object
 */
function getErrorType(error: unknown): string {
  if (error instanceof TypeError) return 'TypeError'
  if (error instanceof Error) return error.name
  if (error && typeof error === 'object' && 'name' in error) {
    return String(error.name)
  }
  return 'UnknownError'
}

/**
 * Extract status code from error object
 */
function getStatusCode(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    // Axios error format
    if ('response' in error && error.response && typeof error.response === 'object' && 'status' in error.response) {
      return error.response.status as number
    }
    // Direct statusCode property
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      return error.statusCode
    }
    // AWS SDK format
    if ('$metadata' in error && error.$metadata && typeof error.$metadata === 'object' && 'httpStatusCode' in error.$metadata) {
      return (error.$metadata as any).httpStatusCode as number
    }
  }
  return undefined
}

/**
 * Generate a unique error ID for tracking
 */
function generateErrorId(): string {
  return `ERR_${Date.now()}_${Math.random().toString(36).substring(2, 9).toUpperCase()}`
}

/**
 * Log an error with structured format and automatic sanitization
 *
 * @param error - The error object to log
 * @param context - Additional context (page, action, etc.)
 * @returns The error ID for reference
 *
 * @example
 * ```typescript
 * try {
 *   await updateBudget(clientCode, budgetData)
 * } catch (error) {
 *   const errorId = logError(error, {
 *     page: 'ClientDashboard',
 *     action: 'updateBudget'
 *   })
 *   console.log('Error ID:', errorId)
 * }
 * ```
 */
export function logError(
  error: unknown,
  context: {
    page: string
    action: string
    additionalInfo?: Record<string, any>
  }
): string {
  const errorId = generateErrorId()
  const timestamp = new Date().toISOString()
  const errorType = getErrorType(error)
  const statusCode = getStatusCode(error)

  // Extract message safely
  let message = 'Unknown error'
  if (error instanceof Error) {
    message = error.message
  } else if (error && typeof error === 'object' && 'message' in error) {
    message = String(error.message)
  } else if (typeof error === 'string') {
    message = error
  }

  // Sanitize message
  const sanitizedMessage = sanitizeString(message)

  // Create structured log entry
  const logEntry: ErrorLogEntry = {
    timestamp,
    page: context.page,
    action: context.action,
    errorType,
    message: sanitizedMessage,
    statusCode,
    errorId
  }

  // Sanitize additional info if provided
  const sanitizedAdditionalInfo = context.additionalInfo
    ? sanitizeObject(context.additionalInfo)
    : undefined

  // Log based on environment
  if (import.meta.env.DEV) {
    // Development: Log more detailed information
    console.group(`🚨 Error [${errorId}]`)
    console.error('Log Entry:', logEntry)
    if (sanitizedAdditionalInfo) {
      console.error('Additional Info:', sanitizedAdditionalInfo)
    }
    // Show original error in dev for debugging (but warn it might contain sensitive data)
    console.warn('⚠️ Original error (may contain sensitive data):')
    console.error(error)
    if (error instanceof Error && error.stack) {
      console.error('Stack trace:', error.stack)
    }
    console.groupEnd()
  } else {
    // Production: Log only sanitized structured data
    console.error(JSON.stringify({
      ...logEntry,
      ...(sanitizedAdditionalInfo && { additionalInfo: sanitizedAdditionalInfo })
    }))
  }

  return errorId
}

/**
 * Log a validation error with field-specific context
 *
 * @param fieldName - The form field that failed validation
 * @param errorMessage - The validation error message
 * @param context - Page and action context
 */
export function logValidationError(
  fieldName: string,
  errorMessage: string,
  context: {
    page: string
    action: string
  }
): string {
  return logError(
    new Error(`Validation error on field "${fieldName}": ${errorMessage}`),
    {
      ...context,
      additionalInfo: {
        fieldName,
        errorType: 'validation'
      }
    }
  )
}

/**
 * Log a network error
 */
export function logNetworkError(
  endpoint: string,
  error: unknown,
  context: {
    page: string
    action: string
  }
): string {
  return logError(error, {
    ...context,
    additionalInfo: {
      endpoint: sanitizeString(endpoint),
      errorType: 'network'
    }
  })
}

/**
 * Log an authentication/authorization error
 */
export function logAuthError(
  errorType: 'authentication' | 'authorization',
  error: unknown,
  context: {
    page: string
    action: string
  }
): string {
  return logError(error, {
    ...context,
    additionalInfo: {
      authErrorType: errorType
    }
  }
)
}

/**
 * Utility to check if an error contains sensitive data (for testing)
 * @internal
 */
export function _containsSensitiveData(value: string): boolean {
  return (
    SENSITIVE_PATTERNS.email.test(value) ||
    SENSITIVE_PATTERNS.currency.test(value) ||
    SENSITIVE_PATTERNS.largeNumbers.test(value) ||
    SENSITIVE_PATTERNS.jwtToken.test(value) ||
    SENSITIVE_PATTERNS.bearerToken.test(value) ||
    SENSITIVE_PATTERNS.awsAccessKey.test(value)
  )
}

/**
 * Export sanitization functions for testing
 * @internal
 */
export const _internal = {
  sanitizeString,
  sanitizeObject,
  SENSITIVE_PATTERNS,
  SENSITIVE_FIELDS
}
